import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, Link2, MessageCircle, Plus, Send, ShieldCheck, Upload, X } from "lucide-react"
import { InstagramIcon } from "@/components/brand/instagram-icon"
import { toast } from "sonner"
import { PERMISSION_LABELS, PROVIDER_LABELS, type ActivityItem, type ConsentEvent, type ContactDetail, type Note, type Tag } from "@vienna/contracts"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Header, Hint, Section, SelectField, Status } from "@/components/audience/primitives"
import { api, ApiError } from "@/lib/api"
import { formatDateTime, initials } from "@/lib/format"
import { contactQuery, fieldsQuery, tagsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/contacts/$contactId")({
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(contactQuery(params.workspace, params.contactId)), context.queryClient.ensureQueryData(fieldsQuery(params.workspace)), context.queryClient.ensureQueryData(tagsQuery(params.workspace))]),
  component: ContactProfile,
})

const ACTIVITY_ICONS: Record<ActivityItem["kind"], typeof MessageCircle> = { message: MessageCircle, comment: MessageCircle, consent: ShieldCheck, identity: Link2, import: Upload, note: MessageCircle, data_request: ShieldCheck }

function longDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
}

/** Screen 31. */
function ContactProfile() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const canEdit = can("reply") || can("edit_automations")
  const { contactId } = Route.useParams()
  const queryClient = useQueryClient()
  const contact = useSuspenseQuery(contactQuery(workspace.slug, contactId)).data
  const fields = useSuspenseQuery(fieldsQuery(workspace.slug)).data.filter((f) => f.origin === "workspace")
  const allTags = useSuspenseQuery(tagsQuery(workspace.slug)).data
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ displayName: contact.displayName, email: contact.email ?? "", city: contact.city ?? "", timezone: contact.timezone ?? "", fields: { ...contact.fields } })
  const [note, setNote] = useState("")
  const [tagChoice, setTagChoice] = useState("")
  const [now] = useState(() => Date.now())
  const params = { workspace: workspace.slug }
  const inboxConversation = contact.identities.find((i) => i.conversationId)?.conversationId ?? null
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "contacts"] })
  const describe = (error: unknown) => (error instanceof ApiError ? error.problem.title : "Something went wrong")

  const update = useMutation({
    mutationFn: () => api<ContactDetail>(`/w/${workspace.slug}/contacts/${contactId}`, { method: "PATCH", body: JSON.stringify({ displayName: draft.displayName, email: draft.email || null, city: draft.city || null, timezone: draft.timezone || null, fields: draft.fields }) }),
    onSuccess: async () => {
      await refresh()
      setEditing(false)
      toast.success("Contact updated")
    },
    onError: (error) => toast.error(describe(error)),
  })
  const addNote = useMutation({
    mutationFn: () => api<Note>(`/w/${workspace.slug}/contacts/${contactId}/notes`, { method: "POST", body: JSON.stringify({ body: note }) }),
    onSuccess: async () => {
      await refresh()
      setNote("")
      toast.success("Note saved")
    },
    onError: (error) => toast.error(describe(error)),
  })
  const tag = useMutation({
    mutationFn: (input: { tagId?: string; tagName?: string }) => api<Tag>(`/w/${workspace.slug}/contacts/tag`, { method: "POST", body: JSON.stringify({ contactIds: [contactId], ...input }) }),
    onSuccess: async (t) => {
      await refresh()
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "tags"] })
      setTagChoice("")
      toast.success(`Tagged with ${t.name}`)
    },
    onError: (error) => toast.error(describe(error)),
  })
  const untag = useMutation({
    mutationFn: (tagId: string) => api<void>(`/w/${workspace.slug}/contacts/${contactId}/tags/${tagId}`, { method: "DELETE" }),
    onSuccess: () => refresh(),
    onError: (error) => toast.error(describe(error)),
  })
  const consent = useMutation({
    mutationFn: (input: { provider: "instagram" | "messenger"; identityId?: string; status: "granted" | "revoked" }) =>
      api<ConsentEvent>(`/w/${workspace.slug}/contacts/${contactId}/consent`, { method: "POST", body: JSON.stringify({ ...input, source: "manual", evidence: `Recorded by a team member on ${new Date().toLocaleDateString()}` }) }),
    onSuccess: async () => {
      await refresh()
      toast.success("Permission recorded with source and time")
    },
    onError: (error) => toast.error(describe(error)),
  })

  const byProvider = { instagram: contact.consentEvents.filter((c) => c.provider === "instagram"), messenger: contact.consentEvents.filter((c) => c.provider === "messenger") }
  const untagged = allTags.filter((t) => !contact.tags.some((c) => c.id === t.id))

  return (
    <div className="page-wrap au-root">
      <Header title={contact.displayName} description={`Contact · Created ${longDate(contact.createdAt)} · ${contact.source.startsWith("import") ? "Imported" : "From a conversation"}`} back={<Link to="/w/$workspace/contacts" params={params} search={{}}><ArrowLeft /> Contacts</Link>}>
        {canEdit && (
          <Button variant="outline" onClick={() => setEditing(true)}>
            Edit contact
          </Button>
        )}
        {inboxConversation ? (
          <Button asChild>
            <Link to="/w/$workspace/inbox" params={params}>
              Open in inbox <ArrowRight />
            </Link>
          </Button>
        ) : (
          <Button disabled>No conversation yet</Button>
        )}
      </Header>
      <div className="au-profile">
        <aside>
          <Card>
            <CardContent>
              <Avatar className="mb-4 size-14">
                <AvatarFallback className="text-lg">{initials(contact.displayName)}</AvatarFallback>
              </Avatar>
              <h2 className="text-lg font-medium">{contact.displayName}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{contact.email ?? contact.detail ?? "No email"}</p>
              {(contact.city || contact.timezone) && <p className="mt-2 text-xs text-muted-foreground">{[contact.city, contact.timezone].filter(Boolean).join(" · ")}</p>}
              <Separator className="my-5" />
              <h3 className="mb-3 text-xs font-medium text-muted-foreground">TAGS</h3>
              <div className="flex flex-wrap gap-1.5">
                {contact.tags.map((t) => (
                  <Badge variant="secondary" key={t.id} className="gap-1">
                    {t.name}
                    {canEdit && (
                      <button aria-label={`Remove ${t.name}`} onClick={() => untag.mutate(t.id)} className="opacity-60 hover:opacity-100">
                        <X className="size-3" />
                      </button>
                    )}
                  </Badge>
                ))}
                {contact.tags.length === 0 && <span className="text-xs text-muted-foreground">No tags yet</span>}
              </div>
              {canEdit && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1">
                    <SelectField label="Add tag" value={tagChoice} onChange={setTagChoice} options={[{ value: "", label: "Add a tag…" }, ...untagged.map((t) => ({ value: t.id, label: t.name })), { value: "__followup", label: "Follow up (new)" }]} />
                  </div>
                  <Button variant="ghost" size="sm" disabled={!tagChoice || tag.isPending} onClick={() => tag.mutate(tagChoice === "__followup" ? { tagName: "Follow up" } : { tagId: tagChoice })}>
                    <Plus />
                    Add
                  </Button>
                </div>
              )}
              <Separator className="my-5" />
              <dl className="space-y-4">
                {fields.map((field) => (
                  <div key={field.key}>
                    <dt className="text-xs text-muted-foreground">{field.label}</dt>
                    <dd className="mt-1 text-sm">{contact.fields[field.key] !== undefined && contact.fields[field.key] !== null && contact.fields[field.key] !== "" ? String(contact.fields[field.key]) : "—"}</dd>
                  </div>
                ))}
                {contact.referenceHandles["instagram"] && (
                  <div>
                    <dt className="text-xs text-muted-foreground">Unverified Instagram handle</dt>
                    <dd className="mt-1 text-sm">{contact.referenceHandles["instagram"]}</dd>
                  </div>
                )}
                {fields.length === 0 && !contact.referenceHandles["instagram"] && <p className="text-xs text-muted-foreground">Add contact fields in Settings to capture more context.</p>}
              </dl>
              {can("manage_customer_data") && (
                <Button variant="outline" className="mt-6 w-full" asChild>
                  <Link to="/w/$workspace/settings/consent" params={params} search={{ contact: contactId }}>
                    Data controls
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        </aside>
        <section className="min-w-0">
          <Tabs defaultValue="activity">
            <TabsList variant="line" className="mb-5">
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="notes">
                Notes <span className="text-xs">{contact.notes.length}</span>
              </TabsTrigger>
              <TabsTrigger value="permission">Permission history</TabsTrigger>
            </TabsList>
            <TabsContent value="activity">
              {contact.activity.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
              <div className="au-timeline">
                {contact.activity.map((item) => {
                  const Icon = ACTIVITY_ICONS[item.kind] ?? (item.kind === "message" ? Send : MessageCircle)
                  return (
                    <div className="au-event" key={item.id}>
                      <span className="au-event-icon">
                        <Icon className="size-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex justify-between gap-2">
                          {item.conversationId ? (
                            <Link className="text-left text-sm font-medium" to="/w/$workspace/inbox" params={params}>
                              {item.title}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium">{item.title}</span>
                          )}
                          <time className="shrink-0 text-xs text-muted-foreground">{formatDateTime(item.occurredAt)}</time>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>
                        {item.body && <p className="mt-3 rounded-lg bg-muted p-3 text-sm leading-5">{item.body}</p>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </TabsContent>
            <TabsContent value="notes">
              <div className="space-y-3">
                {contact.notes.map((n) => (
                  <div key={n.id} className="rounded-lg border p-4">
                    <p className="text-sm leading-6">{n.body}</p>
                    <p className="mt-3 text-xs text-muted-foreground">
                      {n.authorName} · Internal note · {formatDateTime(n.createdAt)}
                    </p>
                  </div>
                ))}
                {canEdit && (
                  <>
                    <Label htmlFor="contact-note">Add an internal note</Label>
                    <Textarea id="contact-note" placeholder="Context for your team…" value={note} onChange={(event) => setNote(event.target.value)} />
                    <Button disabled={!note.trim() || addNote.isPending} onClick={() => addNote.mutate()}>
                      Save note
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>
            <TabsContent value="permission">
              <Section title="Permission history">
                <div className="space-y-5">
                  {(["instagram", "messenger"] as const).map((provider, index) => {
                    const latest = byProvider[provider][0]
                    const identity = contact.identities.find((i) => i.provider === provider && i.scope === "messaging")
                    return (
                      <div key={provider}>
                        {index > 0 && <Separator className="mb-5" />}
                        <Status>
                          {PROVIDER_LABELS[provider]} · {PERMISSION_LABELS[latest ? latest.status === "granted" ? "granted" : "revoked" : "unknown"]}
                        </Status>
                        {latest ? (
                          <>
                            <p className="mt-3 text-sm">
                              {latest.purpose === "marketing" ? "Marketing messages" : latest.purpose} · {formatDateTime(latest.occurredAt)}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Source: {latest.source}
                              {latest.evidence ? ` · ${latest.evidence}` : ""} · recorded by {latest.recordedByName}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="mt-3 text-sm">No marketing permission recorded.</p>
                            <p className="mt-1 text-xs text-muted-foreground">Linking accounts does not transfer permission.</p>
                          </>
                        )}
                        {byProvider[provider].length > 1 && (
                          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                            {byProvider[provider].slice(1).map((e) => (
                              <li key={e.id}>
                                {e.status === "granted" ? "Granted" : "Revoked"} · {formatDateTime(e.occurredAt)} · {e.source}
                              </li>
                            ))}
                          </ul>
                        )}
                        {can("manage_support") && (
                          <div className="mt-3 flex gap-2">
                            <Button variant="outline" size="sm" disabled={consent.isPending || latest?.status === "granted"} onClick={() => consent.mutate({ provider, ...(identity ? { identityId: identity.id } : {}), status: "granted" })}>
                              Record opt-in
                            </Button>
                            <Button variant="ghost" size="sm" disabled={consent.isPending || !latest || latest.status === "revoked"} onClick={() => consent.mutate({ provider, ...(identity ? { identityId: identity.id } : {}), status: "revoked" })}>
                              Record opt-out
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </Section>
            </TabsContent>
          </Tabs>
        </section>
        <aside>
          <Section title="Channel identities">
            {contact.identities.length === 0 && <p className="text-sm text-muted-foreground">No verified channel identity yet. {contact.referenceHandles["instagram"] ? "The imported handle is reference data only." : ""}</p>}
            {contact.identities.map((identity, index) => {
              const windowOpen = identity.supportWindowEndsAt ? new Date(identity.supportWindowEndsAt).getTime() > now : false
              return (
                <div key={identity.id}>
                  {index > 0 && <Separator className="my-5" />}
                  <div className="flex items-center gap-2 font-medium">
                    {identity.provider === "instagram" ? <InstagramIcon className="size-4" /> : <MessageCircle className="size-4" />} {PROVIDER_LABELS[identity.provider]}
                    {identity.scope === "comments" && <Badge variant="outline">comments</Badge>}
                  </div>
                  <p className="mt-2 text-sm">{identity.handle ?? identity.displayName ?? identity.externalActorId}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {identity.connectionName} · #{identity.externalActorId.slice(-6)}
                  </p>
                  <div className="mt-3">
                    <Status>Marketing {PERMISSION_LABELS[identity.marketing].toLowerCase()}</Status>
                  </div>
                  {identity.scope === "messaging" && (
                    <>
                      <p className="mt-5 text-sm font-medium">{windowOpen ? "Support reply available" : "Support reply closed"}</p>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">{windowOpen && identity.supportWindowEndsAt ? `Window ends ${formatDateTime(identity.supportWindowEndsAt)}. Rechecked before sending.` : "No eligible messaging window currently."}</p>
                    </>
                  )}
                </div>
              )
            })}
            <Separator className="my-5" />
            <Hint>These identities are linked by verified ownership, never by name alone.</Hint>
          </Section>
        </aside>
      </div>
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit contact</DialogTitle>
            <DialogDescription>Update profile details. Verified identities stay separate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full name</Label>
              <Input id="edit-name" value={draft.displayName} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input id="edit-email" type="email" value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-city">City</Label>
                <Input id="edit-city" value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tz">Timezone</Label>
                <Input id="edit-tz" value={draft.timezone} placeholder="Europe/London" onChange={(event) => setDraft({ ...draft, timezone: event.target.value })} />
              </div>
            </div>
            {fields.map((field) => (
              <div className="space-y-2" key={field.key}>
                <Label htmlFor={`edit-${field.key}`}>{field.label}</Label>
                {field.type === "single_select" ? (
                  <SelectField label={field.label} value={String(draft.fields[field.key] ?? "")} onChange={(value) => setDraft({ ...draft, fields: { ...draft.fields, [field.key]: value || null } })} options={[{ value: "", label: "—" }, ...field.options.map((o) => ({ value: o, label: o }))]} />
                ) : (
                  <Input id={`edit-${field.key}`} type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"} value={String(draft.fields[field.key] ?? "")} onChange={(event) => setDraft({ ...draft, fields: { ...draft.fields, [field.key]: event.target.value || null } })} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button disabled={!draft.displayName.trim() || update.isPending} onClick={() => update.mutate()}>
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
